import {useCallback, useState} from "react";
import {platformAPI} from "../../../api/axiosConfig";
import {PaginatedResponseV2} from "../../../types/PaginatedResponse";
import {Contact} from "../typedefs";
import {useQuery} from "react-query";
import {useAuth} from "../../../context/user";
import {useSiren} from "../SirenContext";
import useDebouncer from "../../../hooks/debouncer";
import {FilterQueryParams} from "../../../components/paginated-selector/filters/types";

export async function fetchContacts(page: number, limit: number, search: string, clientID: string | null, archived: boolean, departmentID?: string | null): Promise<PaginatedResponseV2<Contact>> {
    const res = await platformAPI.get<PaginatedResponseV2<Contact>>("api/contacts", {
        params: {page, limit, search, archived, ...(clientID ? {clientID} : {}), ...(departmentID ? {departmentID} : {})},
    });
    return res.data;
}

export function useGetContacts(archived: boolean) {
    const {clientID} = useSiren();
    const auth = useAuth();

    const [page, setPage] = useState(1);
    const [limit] = useState(20);
    const [search, setSearch] = useState("");
    const debouncedSearch = useDebouncer(search, 300);

    const {data, isLoading, error, refetch} = useQuery(
        ["contacts", clientID, page, limit, debouncedSearch, archived],
        () => fetchContacts(page, limit, debouncedSearch, clientID, archived),
        {
            enabled: !!clientID || !auth.isSuperAdmin,
            cacheTime: 0,
            refetchOnMount: true,
        },
    );

    return {page, setPage, limit, search, setSearch, data, isLoading, error, refetch};
}

export function useGetContactsList(archived: boolean) {
    const {clientID} = useSiren();
    return useCallback(async (search: string, page: number, filterParams?: FilterQueryParams): Promise<PaginatedResponseV2<Contact> | null> => {
        const userLinked = filterParams?.userLinked;
        const res = await platformAPI.get<PaginatedResponseV2<Contact>>("api/contacts", {
            params: {
                page, limit: 10, search, archived,
                ...(clientID ? {clientID} : {}),
                ...(userLinked !== undefined ? {userLinked} : {}),
            },
        });
        return res.data;
    }, [clientID, archived]);
}
